import { useState } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { CalculatorCard } from "@/components/CalculatorCard";
import { Activity, Zap, Move, Gauge, Calculator as CalcIcon, Wind, Thermometer, Battery, Droplet, ArrowUpDown, Atom, Lightbulb, Orbit, Search } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import logoLight from "@/assets/logo-light_t.png";
import logoDark from "@/assets/logo-dark_t.png";
import { useTranslation } from "react-i18next";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema } from "@/utils/structuredData";

const highSchoolCalculators = {
  mechanics: [
    {
      title: "Force Calculator",
      description: "Calculate force, mass, and acceleration (F = ma)",
      icon: Activity,
      href: "/calculator/force",
      category: "physics" as const,
    },
    {
      title: "Work Calculator",
      description: "Work done by force over distance (W = F × d)",
      icon: Gauge,
      href: "/calculator/work",
      category: "physics" as const,
    },
    {
      title: "Power Calculator",
      description: "Power from work and time (P = W/t)",
      icon: Zap,
      href: "/calculator/power",
      category: "physics" as const,
    },
    {
      title: "Kinetic Energy",
      description: "Energy of motion (KE = ½mv²)",
      icon: Move,
      href: "/calculator/kinetic-energy",
      category: "physics" as const,
    },
    {
      title: "Potential Energy",
      description: "Gravitational potential energy (PE = mgh)",
      icon: ArrowUpDown,
      href: "/calculator/potential-energy",
      category: "physics" as const,
    },
    {
      title: "Momentum",
      description: "Linear momentum (p = mv)",
      icon: Activity,
      href: "/calculator/momentum",
      category: "physics" as const,
    },
    {
      title: "Acceleration",
      description: "Change in velocity over time (Δv/Δt)",
      icon: CalcIcon,
      href: "/calculator/acceleration",
      category: "physics" as const,
    },
    {
      title: "Speed & Velocity",
      description: "Distance over time (d/t)",
      icon: Move,
      href: "/calculator/speed-velocity",
      category: "physics" as const,
    },
    {
      title: "Free Fall",
      description: "Distance fallen under gravity (d = ½gt²)",
      icon: ArrowUpDown,
      href: "/calculator/free-fall",
      category: "physics" as const,
    },
    {
      title: "Projectile Motion",
      description: "Range, height, and time of flight",
      icon: Move,
      href: "/calculator/projectile-motion",
      category: "physics" as const,
    },
  ],
  waves: [
    {
      title: "Wave Speed",
      description: "Wave velocity (v = fλ)",
      icon: Wind,
      href: "/calculator/wave-speed",
      category: "physics" as const,
    },
    {
      title: "Frequency & Wavelength",
      description: "Convert between frequency and wavelength",
      icon: Wind,
      href: "/calculator/frequency-wavelength",
      category: "physics" as const,
    },
    {
      title: "Doppler Effect",
      description: "Frequency shift due to motion",
      icon: Wind,
      href: "/calculator/doppler-effect",
      category: "physics" as const,
    },
  ],
  thermodynamics: [
    {
      title: "Temperature Converter",
      description: "Convert between Celsius, Kelvin, Fahrenheit",
      icon: Thermometer,
      href: "/calculator/temperature-converter",
      category: "physics" as const,
    },
    {
      title: "Heat Transfer",
      description: "Heat energy (Q = mcΔT)",
      icon: Thermometer,
      href: "/calculator/heat-transfer",
      category: "physics" as const,
    },
    {
      title: "Thermal Expansion",
      description: "Linear and volumetric expansion",
      icon: Thermometer,
      href: "/calculator/thermal-expansion",
      category: "physics" as const,
    },
  ],
  electricity: [
    {
      title: "Ohm's Law",
      description: "Voltage, current, and resistance (V = IR)",
      icon: Zap,
      href: "/calculator/ohms-law",
      category: "physics" as const,
    },
    {
      title: "Power in Circuits",
      description: "Electrical power (P = IV)",
      icon: Battery,
      href: "/calculator/circuit-power",
      category: "physics" as const,
    },
    {
      title: "Resistor Color Code",
      description: "Decode resistor color bands",
      icon: Battery,
      href: "/calculator/resistor-color",
      category: "physics" as const,
    },
    {
      title: "Series/Parallel Resistance",
      description: "Calculate equivalent resistance",
      icon: Battery,
      href: "/calculator/resistance-calculator",
      category: "physics" as const,
    },
    {
      title: "Capacitance",
      description: "Capacitor calculations (C = Q/V)",
      icon: Droplet,
      href: "/calculator/capacitance",
      category: "physics" as const,
    },
  ],
};

const advancedCalculators = {
  mechanics: [
    {
      title: "Rotational Motion",
      description: "Torque, angular velocity, and angular acceleration",
      icon: Orbit,
      href: "/calculator/rotational-motion",
      category: "physics" as const,
    },
    {
      title: "Moment of Inertia",
      description: "Calculate rotational inertia for various shapes",
      icon: Orbit,
      href: "/calculator/moment-inertia",
      category: "physics" as const,
    },
    {
      title: "Center of Mass",
      description: "Find center of mass for systems of particles",
      icon: Activity,
      href: "/calculator/center-mass",
      category: "physics" as const,
    },
  ],
  electromagnetism: [
    {
      title: "Coulomb's Law",
      description: "Electrostatic force between charges",
      icon: Zap,
      href: "/calculator/coulombs-law",
      category: "physics" as const,
    },
    {
      title: "Electric Field & Potential",
      description: "Electric field and potential calculations",
      icon: Zap,
      href: "/calculator/electric-field",
      category: "physics" as const,
    },
    {
      title: "Magnetic Force",
      description: "Force on moving charges (F = qvBsinθ)",
      icon: Zap,
      href: "/calculator/magnetic-force",
      category: "physics" as const,
    },
    {
      title: "Faraday's Law",
      description: "Induced EMF and electromagnetic induction",
      icon: Zap,
      href: "/calculator/faradays-law",
      category: "physics" as const,
    },
    {
      title: "RC/RL/RLC Circuit",
      description: "Complex circuit analysis and time constants",
      icon: Battery,
      href: "/calculator/rlc-circuit",
      category: "physics" as const,
    },
  ],
  thermodynamics: [
    {
      title: "Van der Waals",
      description: "Real gas equation solver",
      icon: Thermometer,
      href: "/calculator/van-der-waals",
      category: "physics" as const,
    },
    {
      title: "Entropy Change",
      description: "Calculate entropy changes in processes",
      icon: Thermometer,
      href: "/calculator/entropy-change",
      category: "physics" as const,
    },
    {
      title: "Carnot Efficiency",
      description: "Maximum efficiency of heat engines",
      icon: Gauge,
      href: "/calculator/carnot-efficiency",
      category: "physics" as const,
    },
  ],
  relativity: [
    {
      title: "Time Dilation",
      description: "Special relativity time effects",
      icon: Activity,
      href: "/calculator/time-dilation",
      category: "physics" as const,
    },
    {
      title: "Length Contraction",
      description: "Special relativity length effects",
      icon: Move,
      href: "/calculator/length-contraction",
      category: "physics" as const,
    },
    {
      title: "Relativistic Energy",
      description: "Mass-energy and momentum at high speeds",
      icon: Zap,
      href: "/calculator/relativistic-energy",
      category: "physics" as const,
    },
  ],
  quantum: [
    {
      title: "de Broglie Wavelength",
      description: "Matter wave wavelength calculator",
      icon: Atom,
      href: "/calculator/de-broglie",
      category: "physics" as const,
    },
    {
      title: "Photoelectric Effect",
      description: "Calculate electron ejection from light",
      icon: Lightbulb,
      href: "/calculator/photoelectric-effect",
      category: "physics" as const,
    },
    {
      title: "Heisenberg Uncertainty",
      description: "Position-momentum uncertainty principle",
      icon: Atom,
      href: "/calculator/heisenberg-uncertainty",
      category: "physics" as const,
    },
  ],
  optics: [
    {
      title: "Lens Equation",
      description: "Thin lens calculations (1/f = 1/do + 1/di)",
      icon: Lightbulb,
      href: "/calculator/lens-equation",
      category: "physics" as const,
    },
    {
      title: "Mirror Equation",
      description: "Curved mirror image calculations",
      icon: Lightbulb,
      href: "/calculator/mirror-equation",
      category: "physics" as const,
    },
    {
      title: "Snell's Law",
      description: "Refraction and critical angle",
      icon: Lightbulb,
      href: "/calculator/snells-law",
      category: "physics" as const,
    },
    {
      title: "Diffraction Grating",
      description: "Calculate wavelength from diffraction patterns",
      icon: Lightbulb,
      href: "/calculator/diffraction-grating",
      category: "physics" as const,
    },
  ],
};

export default function Physics() {
  const { t } = useTranslation();
  const [level, setLevel] = useState<"simple" | "advanced">("simple");
  
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: t('categories.pages.physics.title'), url: "https://calclab.app/physics" }
    ], lang)
  ]);
  
  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{t('categories.pages.physics.title')}</h1>
          <p className="text-muted-foreground">
            {t('categories.pages.physics.description')}
          </p>
        </div>

        <div className="flex justify-center gap-2 mb-6">
          <Button
            variant={level === "simple" ? "default" : "outline"}
            onClick={() => setLevel("simple")}
          >
            {t('categories.pages.physics.simple')}
          </Button>
          <Button
            variant={level === "advanced" ? "default" : "outline"}
            onClick={() => setLevel("advanced")}
          >
            {t('categories.pages.physics.advanced')}
          </Button>
        </div>

        {level === "simple" ? (
          <Tabs defaultValue="mechanics" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-8">
              <TabsTrigger value="mechanics">Mechanics</TabsTrigger>
              <TabsTrigger value="waves">Waves</TabsTrigger>
              <TabsTrigger value="thermodynamics">Thermo</TabsTrigger>
              <TabsTrigger value="electricity">Electricity</TabsTrigger>
            </TabsList>

          <TabsContent value="mechanics" className="space-y-4">
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-2">Mechanics</h2>
              <p className="text-muted-foreground">
                Force, energy, motion, and momentum calculators
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {highSchoolCalculators.mechanics.map((calc) => (
                <CalculatorCard key={calc.title} {...calc} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="waves" className="space-y-4">
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-2">Waves & Sound</h2>
              <p className="text-muted-foreground">
                Wave properties, frequency, and doppler effect
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {highSchoolCalculators.waves.map((calc) => (
                <CalculatorCard key={calc.title} {...calc} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="thermodynamics" className="space-y-4">
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-2">Thermodynamics</h2>
              <p className="text-muted-foreground">
                Temperature, heat transfer, and thermal properties
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {highSchoolCalculators.thermodynamics.map((calc) => (
                <CalculatorCard key={calc.title} {...calc} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="electricity" className="space-y-4">
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-2">Electricity & Magnetism</h2>
              <p className="text-muted-foreground">
                Circuit analysis, resistance, and capacitance
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {highSchoolCalculators.electricity.map((calc) => (
                <CalculatorCard key={calc.title} {...calc} />
              ))}
            </div>
          </TabsContent>
        </Tabs>
        ) : (
          <Tabs defaultValue="mechanics" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-6 mb-8">
              <TabsTrigger value="mechanics">Mechanics</TabsTrigger>
              <TabsTrigger value="electromagnetism">E&M</TabsTrigger>
              <TabsTrigger value="thermodynamics">Thermo</TabsTrigger>
              <TabsTrigger value="relativity">Relativity</TabsTrigger>
              <TabsTrigger value="quantum">Quantum</TabsTrigger>
              <TabsTrigger value="optics">Optics</TabsTrigger>
            </TabsList>

            <TabsContent value="mechanics" className="space-y-4">
              <div className="mb-4">
                <h2 className="text-2xl font-bold mb-2">Advanced Mechanics & Dynamics</h2>
                <p className="text-muted-foreground">
                  Rotational motion, inertia, and advanced dynamics
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.mechanics.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="electromagnetism" className="space-y-4">
              <div className="mb-4">
                <h2 className="text-2xl font-bold mb-2">Electromagnetism</h2>
                <p className="text-muted-foreground">
                  Electric fields, magnetic forces, and circuit analysis
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.electromagnetism.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="thermodynamics" className="space-y-4">
              <div className="mb-4">
                <h2 className="text-2xl font-bold mb-2">Advanced Thermodynamics</h2>
                <p className="text-muted-foreground">
                  Real gases, entropy, and heat engine efficiency
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.thermodynamics.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="relativity" className="space-y-4">
              <div className="mb-4">
                <h2 className="text-2xl font-bold mb-2">Special Relativity</h2>
                <p className="text-muted-foreground">
                  Time dilation, length contraction, and relativistic effects
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.relativity.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="quantum" className="space-y-4">
              <div className="mb-4">
                <h2 className="text-2xl font-bold mb-2">Quantum Physics</h2>
                <p className="text-muted-foreground">
                  Wave-particle duality and quantum mechanics
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.quantum.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="optics" className="space-y-4">
              <div className="mb-4">
                <h2 className="text-2xl font-bold mb-2">Optics</h2>
                <p className="text-muted-foreground">
                  Lenses, mirrors, refraction, and diffraction
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advancedCalculators.optics.map((calc) => (
                  <CalculatorCard key={calc.title} {...calc} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </PageLayout>
  );
}
